import type { BriefBody } from '../../types'
import { ContactCard } from './parts'

/** The "Stakeholders" tab under the account flow: economic buyers, org columns, incumbents. */
export function StakeholdersPanel({ body }: { body: BriefBody }) {
  const om = body.orgmap ?? {}

  return (
    <>
      <section className="sec" style={{ paddingTop: 0 }}>
        <div className="sec-h">
          <span className="n">★</span> Economic buyers
        </div>
        <div className="orgleaders">
          {(om.leaders ?? []).length ? (
            om.leaders!.map((c, i) => <ContactCard contact={c} key={i} />)
          ) : (
            <div className="ov-empty">No senior leader identified publicly</div>
          )}
        </div>
      </section>

      <div className="orgcols">
        <div className="orgcol">
          <div className="orgcol-h">Technology</div>
          {(om.technology ?? []).length ? (
            om.technology!.map((c, i) => <ContactCard contact={c} key={i} />)
          ) : (
            <div className="ov-empty">No roles identified</div>
          )}
        </div>
        <div className="orgcol">
          <div className="orgcol-h">Product &amp; Delivery</div>
          {(om.product ?? []).length ? (
            om.product!.map((c, i) => <ContactCard contact={c} key={i} />)
          ) : (
            <div className="ov-empty">No roles identified</div>
          )}
        </div>
      </div>

      <div className="orgmeta" style={{ marginTop: 14 }}>
        {om.emailFormat && (
          <>
            Email format: <code>{om.emailFormat}</code>
          </>
        )}
        {om.domain && (
          <>
            {' '}
            &nbsp;·&nbsp; domain <code>{om.domain}</code>
          </>
        )}
      </div>
      <div className="bnote">
        Names and emails are <b>inferred</b> from public sources — unverified starting points. Verify before
        outreach.
      </div>

      <section className="sec">
        <div className="sec-h">
          <span className="n">⌗</span> Incumbent suppliers &amp; partners
        </div>
        <div className="two">
          {(body.suppliers ?? []).length ? (
            body.suppliers!.map((s, i) => (
              <div className="cc" key={i}>
                <div className="cc-name">{s.name || s.scope || '—'}</div>
                {s.name && s.scope && <div className="cc-role">{s.scope}</div>}
                {s.note && <div className="cc-why">{s.note}</div>}
              </div>
            ))
          ) : (
            <div className="ov-empty">No incumbent suppliers identified</div>
          )}
        </div>
      </section>
    </>
  )
}

/** The "Signals" tab: buying signals, the score breakdown, and the route to market. */
export function SignalsPanel({ body }: { body: BriefBody }) {
  return (
    <>
      <section className="sec" style={{ paddingTop: 0 }}>
        <div className="sec-h">
          <span className="n">↗</span> Transformation &amp; buying signals
        </div>
        <ul className="clean siglist">
          {(body.signals ?? []).length ? (
            body.signals!.map((s, i) => <li key={i}>{s}</li>)
          ) : (
            <li className="ov-empty">No signals captured</li>
          )}
        </ul>
      </section>

      <section className="sec">
        <div className="sec-h">
          <span className="n">#</span> Opportunity score breakdown
        </div>
        <div className="sbk">
          {(body.scoreBreakdown ?? []).map((r, i) => (
            <div className="sbk-row" key={i}>
              <span className="sbk-sig">{r.sig}</span>
              <span className="sbk-bar">
                <i style={{ width: `${Math.min(100, (r.pts / 25) * 100)}%` }} />
              </span>
              <span className="sbk-pts">+{r.pts}</span>
            </div>
          ))}
        </div>
        <div className="reason">{body.scoreReason}</div>
      </section>

      <section className="sec">
        <div className="sec-h">
          <span className="n">⛬</span> Procurement frameworks &amp; route to market
        </div>
        <div className="two">
          {(body.frameworks ?? []).length ? (
            body.frameworks!.map((f, i) => (
              <div className="cc" key={i}>
                <div className="cc-name">
                  {f.name}
                  {f.type && <span className="captag"> {f.type}</span>}
                </div>
                {f.note && <div className="cc-why">{f.note}</div>}
              </div>
            ))
          ) : (
            <div className="ov-empty">No frameworks identified</div>
          )}
        </div>
      </section>
    </>
  )
}
